/**
 * Fetch wrapper with auth headers + retry + error normalisation
 */

import { getAccessToken, getRefreshToken, storeAuth, clearAuth } from './auth.js';
import type { ApiResult, AuthTokens, User } from '../types/index.js';

const BASE = '/api';

// Refresh lock to prevent concurrent refresh races
let refreshingPromise: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const resp = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!resp.ok) { clearAuth(); return false; }
    const json = await resp.json() as { success: boolean; data?: { user: User; tokens: AuthTokens } };
    if (!json.success || !json.data) { clearAuth(); return false; }
    storeAuth(json.data.user, json.data.tokens);
    return true;
  } catch {
    clearAuth();
    return false;
  }
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
): Promise<ApiResult<T>> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let resp = await fetch(`${BASE}${path}`, { ...options, headers });

  // Try token refresh on 401
  if (resp.status === 401 && retry) {
    if (!refreshingPromise) {
      refreshingPromise = refreshTokens().finally(() => { refreshingPromise = null; });
    }
    const refreshed = await refreshingPromise;
    if (refreshed) {
      const newToken = getAccessToken();
      resp = await fetch(`${BASE}${path}`, {
        ...options,
        headers: {
          ...headers,
          ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
        },
      });
    }
  }

  const json = await resp.json() as ApiResult<T>;
  return json;
}

// Convenience methods
export const api = {
  get: <T>(path: string) => apiFetch<T>(path, { method: 'GET' }),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
